// UTM Parameter Capture Utility
// Captures and stores UTM parameters from URL for tracking user acquisition sources
// Uses localStorage for cross-session persistence with 30-day protection

const UTM_STORAGE_KEY = 'vaulto_utm_params';
const REFERRER_STORAGE_KEY = 'vaulto_referrer_domain';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const UTM_EXPIRY_DAYS = 30; // Protect first-touch UTM params for 30 days

/**
 * Extract UTM parameters from current URL
 * @returns {Object} Object containing UTM parameters
 */
export const extractUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  const utmData = {};
  
  UTM_PARAMS.forEach(param => {
    const value = params.get(param);
    if (value) {
      utmData[param] = value;
    }
  });
  
  return utmData;
};

/**
 * Extract referrer domain from document.referrer
 * @returns {string|null} Referrer domain or null
 */
export const extractReferrerDomain = () => {
  try {
    const referrer = document.referrer;
    if (!referrer) {
      return null;
    }
    
    // Extract domain from referrer URL
    try {
      const url = new URL(referrer);
      return url.hostname;
    } catch (e) {
      // If URL parsing fails, try to extract domain manually
      const match = referrer.match(/https?:\/\/([^/]+)/);
      return match ? match[1] : null;
    }
  } catch (error) {
    console.error('Failed to extract referrer domain:', error);
    return null;
  }
};

/**
 * Determine acquisition source based on UTM parameters
 * @param {Object} utmParams - UTM parameters object
 * @returns {string} 'organic' or 'non-organic'
 */
export const getAcquisitionSource = (utmParams) => {
  if (!utmParams || Object.keys(utmParams).length === 0) {
    return 'organic';
  }
  
  // If any UTM parameter exists, it's non-organic
  return 'non-organic';
};

/**
 * Check if stored UTM params are still valid (within 30 days)
 * @param {Object} storedData - Stored data object with timestamp
 * @returns {boolean} True if still valid, false if expired
 */
const isUTMDataValid = (storedData) => {
  if (!storedData || !storedData.timestamp) {
    return false;
  }
  
  const storedTime = new Date(storedData.timestamp).getTime();
  const now = Date.now();
  const daysDiff = (now - storedTime) / (1000 * 60 * 60 * 24);
  
  return daysDiff < UTM_EXPIRY_DAYS;
};

/**
 * Store UTM parameters in localStorage with timestamp
 * Only updates if no existing data or if existing data is older than 30 days
 * @param {Object} utmParams - UTM parameters object
 * @param {string} referrerDomain - Referrer domain
 * @returns {boolean} True if stored, false if skipped (protected by 30-day rule)
 */
export const storeUTMParams = (utmParams, referrerDomain = null) => {
  if (!utmParams || Object.keys(utmParams).length === 0) {
    return false;
  }
  
  try {
    // Check existing stored data
    const existingData = getStoredUTMParams();
    
    // If we have existing UTM params that are still valid (within 30 days), don't overwrite
    if (existingData && existingData.utmParams && Object.keys(existingData.utmParams).length > 0) {
      if (isUTMDataValid(existingData)) {
        console.log('UTM params protected by 30-day rule, not overwriting');
        return false;
      }
    }
    
    // Store new UTM params with timestamp
    const dataToStore = {
      utmParams,
      referrerDomain: referrerDomain || existingData?.referrerDomain || null,
      timestamp: new Date().toISOString(),
      acquisitionSource: getAcquisitionSource(utmParams)
    };
    
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(dataToStore));
    
    // Also store referrer domain separately if provided
    if (referrerDomain) {
      localStorage.setItem(REFERRER_STORAGE_KEY, referrerDomain);
    }
    
    return true;
  } catch (error) {
    console.error('Failed to store UTM parameters:', error);
    return false;
  }
};

/**
 * Retrieve stored UTM parameters from localStorage
 * @returns {Object} Stored UTM data with utmParams, referrerDomain, timestamp, and acquisitionSource
 */
export const getStoredUTMParams = () => {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      
      // Check if data is still valid
      if (isUTMDataValid(data)) {
        return data;
      } else {
        // Data expired, but return it anyway (might be useful for historical tracking)
        console.log('Stored UTM data expired but returning for reference');
        return data;
      }
    }
  } catch (error) {
    console.error('Failed to retrieve UTM parameters:', error);
  }
  
  return {
    utmParams: {},
    referrerDomain: null,
    timestamp: null,
    acquisitionSource: 'organic'
  };
};

/**
 * Get stored referrer domain
 * @returns {string|null} Stored referrer domain or null
 */
export const getStoredReferrerDomain = () => {
  try {
    const stored = localStorage.getItem(REFERRER_STORAGE_KEY);
    return stored || null;
  } catch (error) {
    console.error('Failed to retrieve referrer domain:', error);
    return null;
  }
};

/**
 * Capture UTM parameters from URL and store them
 * This should be called when user first lands on the site
 * Implements 30-day protection: won't overwrite existing UTM params if they're less than 30 days old
 * @returns {Object} Captured UTM parameters and metadata
 */
export const captureAndStoreUTM = () => {
  const utmParams = extractUTMParams();
  const referrerDomain = extractReferrerDomain();
  
  // Store UTM params if present (with 30-day protection)
  const stored = storeUTMParams(utmParams, referrerDomain);
  
  // If no UTM params but we have referrer, store referrer separately
  if (Object.keys(utmParams).length === 0 && referrerDomain) {
    try {
      const existingData = getStoredUTMParams();
      // Only store referrer if we don't have one already
      if (!existingData.referrerDomain) {
        localStorage.setItem(REFERRER_STORAGE_KEY, referrerDomain);
      }
    } catch (error) {
      console.error('Failed to store referrer domain:', error);
    }
  }
  
  return {
    utmParams,
    referrerDomain,
    acquisitionSource: getAcquisitionSource(utmParams),
    stored
  };
};

/**
 * Get UTM parameters (from URL or stored)
 * Priority: Current URL > Stored (if within 30 days)
 * @returns {Object} UTM parameters and metadata
 */
export const getUTMParams = () => {
  // First check current URL
  const urlParams = extractUTMParams();
  const referrerDomain = extractReferrerDomain();
  
  if (Object.keys(urlParams).length > 0) {
    // Try to store new params (will be protected by 30-day rule if needed)
    storeUTMParams(urlParams, referrerDomain);
    return {
      utmParams: urlParams,
      referrerDomain: referrerDomain || getStoredReferrerDomain(),
      acquisitionSource: getAcquisitionSource(urlParams)
    };
  }
  
  // Fall back to stored params
  const stored = getStoredUTMParams();
  return {
    utmParams: stored.utmParams || {},
    referrerDomain: stored.referrerDomain || getStoredReferrerDomain(),
    acquisitionSource: stored.acquisitionSource || 'organic'
  };
};

/**
 * Get all acquisition data (UTM params, referrer, acquisition source)
 * @returns {Object} Complete acquisition data
 */
export const getAcquisitionData = () => {
  const stored = getStoredUTMParams();
  const currentReferrer = extractReferrerDomain();
  
  return {
    utmParams: stored.utmParams || {},
    referrerDomain: stored.referrerDomain || currentReferrer || null,
    acquisitionSource: stored.acquisitionSource || getAcquisitionSource(stored.utmParams || {})
  };
};

/**
 * Clear stored UTM parameters
 * Should be called after successful registration
 */
export const clearUTMParams = () => {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(REFERRER_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear UTM parameters:', error);
  }
};

/**
 * Encode UTM parameters and referrer for OAuth state parameter
 * @param {Object} utmParams - UTM parameters object
 * @param {string} referrerDomain - Referrer domain
 * @param {string} acquisitionSource - Acquisition source
 * @returns {string} Base64 encoded JSON string
 */
export const encodeUTMForOAuth = (utmParams, referrerDomain = null, acquisitionSource = null) => {
  const dataToEncode = {
    utmParams: utmParams || {},
    referrerDomain,
    acquisitionSource: acquisitionSource || getAcquisitionSource(utmParams || {})
  };
  
  if (!utmParams || Object.keys(utmParams).length === 0) {
    // Still encode if we have referrer domain
    if (!referrerDomain) {
      return null;
    }
  }
  
  try {
    const json = JSON.stringify(dataToEncode);
    return btoa(json);
  } catch (error) {
    console.error('Failed to encode UTM parameters:', error);
    return null;
  }
};

/**
 * Decode UTM parameters, referrer, and acquisition source from OAuth state parameter
 * @param {string} encodedState - Base64 encoded state string
 * @returns {Object} Object with utmParams, referrerDomain, and acquisitionSource
 */
export const decodeUTMFromOAuth = (encodedState) => {
  if (!encodedState) {
    return {
      utmParams: {},
      referrerDomain: null,
      acquisitionSource: 'organic'
    };
  }
  
  try {
    const json = atob(encodedState);
    const decoded = JSON.parse(json);
    
    // Handle both old format (just UTM params) and new format (with referrer and acquisition source)
    if (decoded.utmParams) {
      return {
        utmParams: decoded.utmParams || {},
        referrerDomain: decoded.referrerDomain || null,
        acquisitionSource: decoded.acquisitionSource || getAcquisitionSource(decoded.utmParams || {})
      };
    } else {
      // Old format - just UTM params
      return {
        utmParams: decoded,
        referrerDomain: null,
        acquisitionSource: getAcquisitionSource(decoded)
      };
    }
  } catch (error) {
    // If decoding fails, it might not be UTM data, return empty object
    return {
      utmParams: {},
      referrerDomain: null,
      acquisitionSource: 'organic'
    };
  }
};

const utmCaptureExports = {
  extractUTMParams,
  extractReferrerDomain,
  getAcquisitionSource,
  storeUTMParams,
  getStoredUTMParams,
  getStoredReferrerDomain,
  captureAndStoreUTM,
  getUTMParams,
  getAcquisitionData,
  clearUTMParams,
  encodeUTMForOAuth,
  decodeUTMFromOAuth
};

export default utmCaptureExports;
